import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { STATUS } from 'projectConstants';
import apiService from 'services';
import bloxBiService from 'services/bloxBiService';

interface DashboardProperties {
  dashboard: any;
  isPageBarOpen: boolean;
  selectedPage: any;
  isPageModalOpen: boolean;
  editPageData: any;
  createPageStatus: keyof typeof STATUS;
  updatePageStatus: keyof typeof STATUS;
  deletePageStatus: keyof typeof STATUS;
  fetchDashboardStatus: keyof typeof STATUS;
  dashboardBlocks: Array<any>;
  dashboardBlockStatus: keyof typeof STATUS;
  selectedDashboardScenario: any;
  compareDashboardScenario: any;
  dashboardInstruments: Array<any>;
  dashboardInstrumentData: any;
  dashboardInstrumentStatus: keyof typeof STATUS;
  editInstrumentStatus: keyof typeof STATUS;
  editInstrumentData: any;
  editInstrumentOutput: any;
  allInstruments: Array<any>;
  timeFilter: { start: string; end: string } | null;
  allInstrumentOutputs: any;
  instrumentExpandKey: any;
  allInstrumentOutputsStatus: keyof typeof STATUS;
  calculationLoadingStatus: keyof typeof STATUS;
  selectedRow: any;
  dashboardReportingPeriod: {
    preset: string;
    start: string;
    end: string;
  };
}

const initialState: DashboardProperties = {
  dashboard: null,
  isPageBarOpen: false,
  selectedPage: null,
  isPageModalOpen: false,
  editPageData: {},
  createPageStatus: STATUS.IDLE,
  updatePageStatus: STATUS.IDLE,
  deletePageStatus: STATUS.IDLE,
  fetchDashboardStatus: STATUS.IDLE,
  dashboardBlocks: [],
  dashboardBlockStatus: STATUS.IDLE,
  selectedDashboardScenario: null,
  compareDashboardScenario: null,
  dashboardInstruments: [],
  dashboardInstrumentData: {},
  dashboardInstrumentStatus: STATUS.IDLE,
  editInstrumentData: {},
  editInstrumentOutput: {},
  editInstrumentStatus: STATUS.IDLE,
  allInstruments: [], // with edit data
  timeFilter: null,
  allInstrumentOutputs: [], // key(insturmentID): value(insturment Data) output data
  instrumentExpandKey: {},
  allInstrumentOutputsStatus: STATUS.IDLE,
  calculationLoadingStatus: STATUS.IDLE,
  selectedRow: null,
  dashboardReportingPeriod: {
    preset: 'Current Month',
    start: '',
    end: '',
  },
};

const updateDashboardblockData = (blocksData: Array<any>, dimItemMap: any) => {
  const returnData = blocksData.map((block: any) => {
    const dims = block.dimensions.map((dimension: any) => {
      const items = dimItemMap[dimension.id] || [];
      return {
        dimension_id: dimension.id,
        dimension_name: dimension.name,
        dimension_items: items,
      };
    });
    return {
      block_id: block.id,
      block_name: block.name,
      indicators: block.indicators.map((indicator: any) => {
        const indDims = (indicator?.indicator_dimension || []).map((dimObject: any) => {
          const newDimension = dims.find((dimId: any) => {
            return dimObject.indicator_dimension_id === dimId.dimension_id;
          });
          return {
            indicator_dimension_id: dimObject.indicator_dimension_id,
            indicator_dimension_name: dimObject.indicator_dimension_name,
            indicator_dimension_items: newDimension?.dimension_items
              ? newDimension?.dimension_items.map((item: any) => {
                  return {
                    indicator_dimension_item_id: item.dimension_item_id,
                    indicator_dimension_item_name: item.dimension_item_name,
                  };
                })
              : [],
          };
        });

        return {
          indicator_id: indicator.id,
          id: indicator.id,
          indicator_name: indicator.name,
          data_type: indicator.data_type,
          data_format: indicator.data_format,
          indicator_dimensions: indDims,
          indicator_dimension_items: [],
          data_format_properties: indicator.data_format_properties,
        };
      }),
      dimensions: dims,
    };
  });

  return returnData;
};
export const FetchModelBlocks = createAsyncThunk(
  'dashboardModal/get',
  async ({ modelId }: any, thunkAPI) => {
    try {
      const blocksData = await apiService.getDashboardBlock(modelId);
      const modelDimensions = await apiService.getBuilderDimension(modelId);
      const dimensions = modelDimensions?.data?.dimensions;
      const dimensionItemsPromises =
        dimensions?.map((dimension: any) => apiService.get_dimension_items(dimension.id)) || [];

      const resolvedDimensionItems = await Promise.all(dimensionItemsPromises);

      const dimItemMap: any = {};
      dimensions.map((dim: any, index: number) => {
        const itemsData = resolvedDimensionItems[index]?.data?.items;
        dimItemMap[dim.id] = itemsData.map((item: any) => {
          return {
            dimension_item_id: item.id,
            dimension_item_name: item.name,
          };
        });
        return dimItemMap;
      });
      const returnData = updateDashboardblockData(blocksData.data.blocks, dimItemMap);
      return returnData;
    } catch (error: any) {
      // todo eslint-disable-next-line no-console
      console.error('Error:', error);
      return thunkAPI.rejectWithValue(error?.response?.data);
    }
  },
);

export const fetchDashboardID = createAsyncThunk(
  'fetchDashboardID/create',
  async ({ model_id }: any, thunkAPI) => {
    try {
      const response = await bloxBiService.fetch_dashboardid(model_id);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);
export const createDashboardID = createAsyncThunk(
  'createDashboardID/create',
  async ({ request_body }: any, thunkAPI) => {
    try {
      const response = await bloxBiService.create_dashboard({ request_body });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const setDashboardTimeFilter = createAsyncThunk(
  'dashboardTimeFilter/update',
  async ({ request_body }: any, thunkAPI) => {
    try {
      console.log('🔄 Redux setDashboardTimeFilter called with:', request_body);
      const response = await bloxBiService.update_dashboard({ request_body });

      return response.data;
    } catch (error: any) {
      console.log('❌ Redux setDashboardTimeFilter error:', error);
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const fetchDashboardMetadata = createAsyncThunk(
  'dashboardMetadata/get',
  async ({ dashboardId }: any, thunkAPI) => {
    try {
      const metadata = await bloxBiService.dashboard_metadata(dashboardId);
      return metadata.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.response?.data);
    }
  },
);

export const createInstrument = createAsyncThunk(
  'createInstrument/post',
  async ({ request_body }: any, thunkAPI) => {
    try {
      const response = await bloxBiService.createDashboardChart({ request_body });

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const updateInstrument = createAsyncThunk(
  'updateInstrument/put',
  async ({ request_body }: any, thunkAPI) => {
    try {
      const response = await bloxBiService.updateDashboardChart({ request_body });

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const deleteInstrument = createAsyncThunk(
  'delete/deleteInstrument',
  async ({ chartId }: any, thunkAPI) => {
    try {
      const response = await bloxBiService.deleteChart(chartId);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const updatelayout = createAsyncThunk(
  'updateLayout/put',
  async (request_body: any, thunkAPI) => {
    try {
      const response = await bloxBiService.updateChartLayout({ request_body });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const fetchDashboardGraph = createAsyncThunk(
  'fetchChartInstrument/get',
  async ({ chartId, scenarioId, requestBody }: any, thunkAPI) => {
    try {
      const response = await bloxBiService.getDashboardGraph(chartId, scenarioId, requestBody);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const fetchAllDataValues = createAsyncThunk(
  'fetchChartInstrument/get',
  async ({ pageId, scenarioId }: any, thunkAPI) => {
    try {
      // Get timeFilter, reportingPeriod, and compareScenario from state
      const state: any = thunkAPI.getState();
      const timeFilter = state.dashboardSliceV2?.timeFilter;
      const dashboardReportingPeriod = state.dashboardSliceV2?.dashboardReportingPeriod;
      const compareDashboardScenario = state.dashboardSliceV2?.compareDashboardScenario;
      const startDate = timeFilter?.start || '';
      const endDate = timeFilter?.end || '';
      const compareScenarioId = compareDashboardScenario?.id || null;
      // Use new API method
      const response = await bloxBiService.getDashboardDataValuesWithTime(
        pageId,
        scenarioId,
        startDate,
        endDate,
        dashboardReportingPeriod,
        compareScenarioId,
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const createPage = createAsyncThunk(
  'createPage/post',
  async ({ request_body }: any, thunkAPI) => {
    try {
      console.log('🔄 Redux createPage called with:', request_body);
      const response = await bloxBiService.create_page({ request_body });
      return response.data;
    } catch (error: any) {
      console.log('❌ Redux createPage error:', error);
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const updatePage = createAsyncThunk(
  'updatePage/put',
  async ({ request_body }: any, thunkAPI) => {
    try {
      console.log('🔄 Redux updatePage called with:', request_body);
      const response = await bloxBiService.update_page({ request_body });
      return response.data;
    } catch (error: any) {
      console.log('❌ Redux updatePage error:', error);
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const updatePageOrder = createAsyncThunk(
  'updatePageOrder/put',
  async ({ dashboard }: { dashboard: any }, thunkAPI) => {
    try {
      // Update the entire dashboard with reordered pages
      const response = await bloxBiService.update_dashboard({
        request_body: dashboard,
      });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const deletePage = createAsyncThunk(
  'deletePage/delete',
  async ({ pageId }: { pageId: string }, thunkAPI) => {
    try {
      console.log('🔄 Redux deletePage called with pageId:', pageId);
      const response = await bloxBiService.delete_page(pageId);
      return { pageId, data: response.data };
    } catch (error: any) {
      console.log('❌ Redux deletePage error:', error);
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

const dashboardSliceV2 = createSlice({
  initialState,
  name: 'dashboardSliceV2',
  reducers: {
    resetDashboardSliceV2: (state) => {
      state.dashboard = null;
      state.isPageBarOpen = false;
      state.selectedPage = null;
      state.isPageModalOpen = false;
      state.editPageData = {};
      state.createPageStatus = STATUS.IDLE;
      state.updatePageStatus = STATUS.IDLE;
      state.fetchDashboardStatus = STATUS.IDLE;
      state.selectedDashboardScenario = null;
      state.compareDashboardScenario = null;
      state.dashboardBlocks = [];
      state.dashboardBlockStatus = STATUS.IDLE;
      state.dashboardInstruments = [];
      state.editInstrumentStatus = STATUS.IDLE;
      state.editInstrumentData = {};
      state.editInstrumentOutput = {};
      state.allInstruments = [];
      state.timeFilter = null;
      state.allInstrumentOutputs = [];
      state.instrumentExpandKey = {};
      state.allInstrumentOutputsStatus = STATUS.IDLE;
      state.calculationLoadingStatus = STATUS.IDLE;
      state.selectedRow = null;
      state.dashboardReportingPeriod = {
        preset: 'Current Month',
        start: '',
        end: '',
      };
    },
    setDashboard: (state, action) => {
      state.dashboard = action.payload;
    },
    toggleIsPageBarOpen: (state, action) => {
      state.isPageBarOpen = !state.isPageBarOpen;
      try {
        const modelId = action?.payload;
        if (modelId) {
          const planFilters = localStorage.getItem('global-modal-filters');
          const data = planFilters ? JSON.parse(planFilters) : {};
          data[modelId] = {
            ...data[modelId],
            isPageBarOpen: state.isPageBarOpen,
          };
          localStorage.setItem('global-modal-filters', JSON.stringify(data));
        }
      } catch {
        // Ignore errors when persisting isPageBarOpen to localStorage
      }
    },
    setIsPageBarOpen: (state, action) => {
      state.isPageBarOpen = action.payload;
    },
    setEditInstrumentOutput: (state, action) => {
      state.editInstrumentOutput = action.payload;
    },
    setAllInstrumentOutputs: (state, action) => {
      state.allInstrumentOutputs = action.payload;
    },
    setTimeFilter: (state, action) => {
      const object: any = { start: action.payload.start, end: action.payload.end };
      state.timeFilter = object;
    },
    setDashboardReportingPeriod: (state, action) => {
      state.dashboardReportingPeriod = {
        preset: action.payload.preset || 'Custom',
        start: action.payload.start || '',
        end: action.payload.end || '',
      };
    },
    selectDashboardScenario: (state, action) => {
      state.selectedDashboardScenario = action.payload;
    },
    selectCompareDashboardScenario: (state, action) => {
      state.compareDashboardScenario = action.payload;
    },
    setDashboardInstrumentStatus: (state, action) => {
      state.dashboardInstrumentStatus = action.payload;
    },
    setEditInstrumentStatus: (state, action) => {
      state.editInstrumentStatus = action.payload;
    },
    setEditInstrumentData: (state, action) => {
      state.editInstrumentData = action.payload;
    },
    setAllInstruments: (state, action) => {
      state.allInstruments = action.payload;
    },
    setInstrumentExpandKey: (state, action) => {
      state.instrumentExpandKey = action.payload;
    },
    setCalculationLocadingStatus: (state, action) => {
      state.calculationLoadingStatus = action.payload;
    },
    setSelectedPage: (state, action) => {
      state.selectedPage = action.payload;
    },
    prepareCreatePayload: (state, action) => {
      state.editInstrumentData = {
        instrument_type: action.payload,
        instrument_indicators: [],
        dashboard_id: state.dashboard?.id,
        start_date: state.timeFilter?.start || 'Jan-24',
        end_date: state.timeFilter?.end || 'Dec-26',
        instrument_filters: [
          {
            instrument_id: null,
            name: 'Time',
            operator: 'between',
            value: state.timeFilter?.start || 'Jan-24',
            second_value: state.timeFilter?.end || 'Dec-26',
            filter_type: 'primary',
          },
          // TODO:  Global Time  Filter
        ],
        time_granularity: 'month', // "Input should be 'all', 'month', 'quarter' or 'year'"
        id: null,
        scenario_id: state.selectedDashboardScenario.id.toString(),
        compare_scenario: state.compareDashboardScenario?.id || null,
        name:
          action.payload === 'line' || action.payload === 'bar' || action.payload === 'stack'
            ? 'Chart'
            : `${action.payload}`.charAt(0).toUpperCase() + `${action.payload}`.slice(1),
        page_id: state.selectedPage?.id,
        // TODO: unused should be removed
        formatting: {
          units: 'auto',
          sparkline: false,
          decimalPlaces: 0,
          stackBar: false,
          negativeDisplay: 'Minus Sign',
          columns_settings: {
            actual_column_name: '',
            previous_year: true,
            varience_to_py: true,
            varience_percentage_py: true,
            plan_comparison_column_name: '',
            plan: true,
            varience_to_pl: true,
            varience_percentage_pl: true,
          },
          columns_position: {
            previous_year: 1,
            varience_to_py: 2,
            varience_percentage_py: 3,
            plan: 4,
            varience_to_pl: 5,
            varience_percentage_pl: 6,
            plan_comparison_column_name: 7,
            actual_column_name: 8,
          },
        },
        reporting_period: {
          preset: 'Current Month',
          start: '',
          end: '',
        },
        instrument_sorts: [],
        instrument_dimensions: [],
        conditional_formatting: [],
        position: 0,
        width: 4,
        height: 2,
      };
    },
    handleSelectedRow: (state, action) => {
      state.selectedRow = action.payload;
    },
    handleEditCancel: (state) => {
      state.editInstrumentData = {};
      state.editInstrumentOutput = {};
      state.selectedRow = null;
    },
    togglePageModal: (state) => {
      state.isPageModalOpen = !state.isPageModalOpen;
      if (!state.isPageModalOpen) {
        state.editPageData = {};
      }
    },
    setEditPageData: (state, action) => {
      state.editPageData = action.payload;
    },
    openPageModal: (state, action) => {
      state.isPageModalOpen = true;
      state.editPageData = action.payload || {};
    },
    closePageModal: (state) => {
      state.isPageModalOpen = false;
      state.editPageData = {};
    },
    reorderPages: (state, action) => {
      if (state.dashboard?.pages) {
        state.dashboard.pages = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(FetchModelBlocks.pending, (state) => {
        state.dashboardBlockStatus = STATUS.LOADING;
      })
      .addCase(FetchModelBlocks.fulfilled, (state, action) => {
        state.dashboardBlocks = action.payload;
        state.dashboardBlockStatus = STATUS.SUCCESS;
      })
      .addCase(fetchDashboardID.fulfilled, (state, action) => {
        state.dashboard = action.payload;
        state.fetchDashboardStatus = STATUS.SUCCESS;
      })
      .addCase(createDashboardID.fulfilled, (state, action) => {
        state.dashboard = action.payload;
        state.fetchDashboardStatus = STATUS.SUCCESS;
      })
      .addCase(fetchDashboardMetadata.fulfilled, (state, action) => {
        // state.dashboard = { ...state.dashboard, ...action.payload };
        state.allInstruments = action.payload?.instruments;
      })
      .addCase(fetchAllDataValues.pending, (state) => {
        state.allInstrumentOutputsStatus = STATUS.LOADING;
      })
      .addCase(fetchAllDataValues.rejected, (state) => {
        state.allInstrumentOutputsStatus = STATUS.FAILED;
      })
      .addCase(fetchAllDataValues.fulfilled, (state, action) => {
        state.allInstrumentOutputs = action.payload;
        state.allInstrumentOutputsStatus = STATUS.SUCCESS;
      })
      .addCase(createInstrument.pending, (state) => {
        state.calculationLoadingStatus = STATUS.LOADING;
      })
      .addCase(createInstrument.fulfilled, (state) => {
        state.calculationLoadingStatus = STATUS.SUCCESS;
      })
      .addCase(createInstrument.rejected, (state) => {
        state.calculationLoadingStatus = STATUS.FAILED;
      })
      .addCase(updateInstrument.pending, (state) => {
        state.calculationLoadingStatus = STATUS.LOADING;
      })
      .addCase(updateInstrument.fulfilled, (state) => {
        state.calculationLoadingStatus = STATUS.SUCCESS;
      })
      .addCase(updateInstrument.rejected, (state) => {
        state.calculationLoadingStatus = STATUS.FAILED;
      })
      .addCase(createPage.pending, (state) => {
        state.createPageStatus = STATUS.LOADING;
      })
      .addCase(createPage.fulfilled, (state, action) => {
        state.createPageStatus = STATUS.SUCCESS;
        // Add the new page to the dashboard pages array
        if (state.dashboard?.pages) {
          state.dashboard.pages.push(action.payload);
        } else if (state.dashboard) {
          state.dashboard.pages = [action.payload];
        }
        // Automatically select the newly created page
        state.selectedPage = action.payload;
        state.isPageModalOpen = false;
        state.editPageData = {};
      })
      .addCase(createPage.rejected, (state) => {
        state.createPageStatus = STATUS.FAILED;
      })
      .addCase(updatePage.pending, (state) => {
        state.updatePageStatus = STATUS.LOADING;
      })
      .addCase(updatePage.fulfilled, (state, action) => {
        state.updatePageStatus = STATUS.SUCCESS;
        // Update the page in the dashboard pages array
        if (state.dashboard?.pages) {
          const pageIndex = state.dashboard.pages.findIndex(
            (page: any) => page.id === action.payload.id,
          );
          if (pageIndex !== -1) {
            state.dashboard.pages[pageIndex] = action.payload;
          }
        }
        state.isPageModalOpen = false;
        state.editPageData = {};
      })
      .addCase(updatePage.rejected, (state) => {
        state.updatePageStatus = STATUS.FAILED;
      })
      .addCase(deletePage.pending, (state) => {
        state.deletePageStatus = STATUS.LOADING;
      })
      .addCase(deletePage.fulfilled, (state, action) => {
        state.deletePageStatus = STATUS.SUCCESS;
        // Remove the page from the dashboard pages array
        if (state.dashboard?.pages) {
          state.dashboard.pages = state.dashboard.pages.filter(
            (page: any) => page.id !== action.payload.pageId,
          );
        }
        // If the deleted page was selected, select the first page or null
        if (state.selectedPage?.id === action.payload.pageId) {
          state.selectedPage = state.dashboard?.pages?.[0] || null;
        }
      })
      .addCase(deletePage.rejected, (state) => {
        state.deletePageStatus = STATUS.FAILED;
      });
  },
});
export const {
  setTimeFilter,
  setDashboardReportingPeriod,
  resetDashboardSliceV2,
  selectDashboardScenario,
  selectCompareDashboardScenario,
  setDashboardInstrumentStatus,
  setEditInstrumentStatus,
  setEditInstrumentData,
  setAllInstruments,
  prepareCreatePayload,
  setEditInstrumentOutput,
  setAllInstrumentOutputs,
  setInstrumentExpandKey,
  setDashboard,
  handleSelectedRow,
  handleEditCancel,
  setCalculationLocadingStatus,
  setSelectedPage,
  toggleIsPageBarOpen,
  setIsPageBarOpen,
  togglePageModal,
  setEditPageData,
  openPageModal,
  closePageModal,
  reorderPages,
} = dashboardSliceV2.actions;
export const getTimeFilter = (state: any) => state.dashboardSliceV2.timeFilter;
export const getDashboardReportingPeriod = (state: any) =>
  state.dashboardSliceV2.dashboardReportingPeriod;
export const getDashboard = (state: any) => state.dashboardSliceV2.dashboard;
export const getDashboardBlocks = (state: any) => state.dashboardSliceV2.dashboardBlocks;
export const getDashboardBlockStatus = (state: any) => state.dashboardSliceV2.dashboardBlockStatus;
export const getAllInstruments = (state: any) => state.dashboardSliceV2.allInstruments;
export const getSelectedDashboardScenario = (state: any) =>
  state.dashboardSliceV2.selectedDashboardScenario;
export const getCompareDashboardScenario = (state: any) =>
  state.dashboardSliceV2.compareDashboardScenario;
export const getFetchDashboardStatus = (state: any) => state.dashboardSliceV2.fetchDashboardStatus;
export const getDashboardInstruments = (state: any) => state.dashboardSliceV2.dashboardInstruments;
export const getDashboardInstrumentStatus = (state: any) =>
  state.dashboardSliceV2.dashboardInstrumentStatus;
export const getEditInstrumentStatus = (state: any) => state.dashboardSliceV2.editInstrumentStatus;
export const getEditInstrumentData = (state: any) => state.dashboardSliceV2.editInstrumentData;
export const getEditInstrumentOutput = (state: any) => state.dashboardSliceV2.editInstrumentOutput;
export const getAllInstrumentOutputs = (state: any) => state.dashboardSliceV2.allInstrumentOutputs;
export const getInstrumentExpandKey = (state: any) => state.dashboardSliceV2.instrumentExpandKey;
export const getCalculationLoadingStatus = (state: any) =>
  state.dashboardSliceV2.calculationLoadingStatus;
export const getAllInstrumentOutputsStatus = (state: any) =>
  state.dashboardSliceV2.allInstrumentOutputsStatus;
export const getSelectedRow = (state: any) => state.dashboardSliceV2.selectedRow;
export const getSelectedPage = (state: any) => state.dashboardSliceV2.selectedPage;
export const getIsPageBarOpen = (state: any) => state.dashboardSliceV2.isPageBarOpen;
export const getIsPageModalOpen = (state: any) => state.dashboardSliceV2.isPageModalOpen;
export const getEditPageData = (state: any) => state.dashboardSliceV2.editPageData;
export const getCreatePageStatus = (state: any) => state.dashboardSliceV2.createPageStatus;
export const getUpdatePageStatus = (state: any) => state.dashboardSliceV2.updatePageStatus;
export const getDeletePageStatus = (state: any) => state.dashboardSliceV2.deletePageStatus;

export default dashboardSliceV2.reducer;
